/**
 * レッスンガイドのデータ形式v1。
 * 保存時（Worker）と表示前（ブラウザ）の両方が同じ検証を通すため、
 * このファイルはDOMにもWorkers型にも依存しない純粋なTypeScriptで書く。
 */

export interface GuideItem {
  /** namespaceを除いたブロック・アイテムID（例: stone_bricks、spyglass）。 */
  block: string;
  /** 表示名。省略時はビューア側で日本語名に変換する。 */
  label?: string;
  count: number;
  /** 役割の説明（例: かべ、ふむスイッチ）。 */
  note?: string;
}

export interface GuideCommand {
  /** 実際に入力するコマンド。`/`で始まる。大文字表記は表示側が常にこれから導出する。 */
  input: string;
  note?: string;
}

export interface GuideImage {
  /** https:// のURLか data:image/ のデータURLのみ。 */
  src: string;
  alt?: string;
}

export interface GuideStep {
  title: string;
  summary?: string;
  /** 番号付きの操作手順。 */
  ops?: string[];
  /** このステップで3Dハイライトするブロック（namespaceなしID）。 */
  highlight?: string[];
  /** このステップで使うブロック・アイテムと個数。 */
  items?: GuideItem[];
  command?: GuideCommand;
  /** できたかな？の確認文。 */
  check?: string;
  /** こまったら の助け舟。 */
  trouble?: string;
  image?: GuideImage;
}

export interface LessonGuide {
  version: 1;
  title: string;
  course?: 'starter' | 'p' | 'challenge';
  lessonId?: string;
  /** きょう つかう 材料の全一覧。空ならじゅんび画面を飛ばしてSTEP 1から始まる。 */
  items: GuideItem[];
  steps: GuideStep[];
}

/** ガイドJSON全体のバイト数上限。データURL画像を含む前提で余裕を持たせる。 */
export const MAX_GUIDE_BYTES = 512 * 1024;
export const MAX_ITEMS = 24;
export const MAX_STEPS = 30;
export const MAX_OPS = 6;
export const MAX_HIGHLIGHT = 8;
export const BLOCK_ID_PATTERN = /^[a-z0-9_]{1,64}$/;

const COURSES = ['starter', 'p', 'challenge'] as const;
const MAX_TEXT = 200;
const MAX_TITLE = 80;
const MAX_COMMAND = 120;
const MAX_IMAGE_SRC = 400_000;

export type GuideValidation = { guide: LessonGuide } | { errors: string[] };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

class Collector {
  errors: string[] = [];

  text(value: unknown, path: string, max: number, required = false): string | undefined {
    if (value === undefined || value === null) {
      if (required) this.errors.push(`${path} は必須です`);
      return undefined;
    }
    if (typeof value !== 'string' || value.trim().length === 0) {
      this.errors.push(`${path} は空でない文字列にしてください`);
      return undefined;
    }
    if (value.length > max) {
      this.errors.push(`${path} は${max}文字以内にしてください`);
      return undefined;
    }
    return value;
  }

  blockId(value: unknown, path: string): string | undefined {
    if (typeof value !== 'string' || !BLOCK_ID_PATTERN.test(value)) {
      this.errors.push(`${path} はnamespaceなしのブロックID（例: stone_bricks）にしてください`);
      return undefined;
    }
    return value;
  }

  count(value: unknown, path: string): number | undefined {
    if (typeof value !== 'number' || !Number.isInteger(value) || value < 1 || value > 999) {
      this.errors.push(`${path} は1〜999の整数にしてください`);
      return undefined;
    }
    return value;
  }
}

/** チェック状態はブロックIDごとに1つなので、同じIDのカードが複数あると表示が壊れる。 */
function checkDuplicateBlocks(items: GuideItem[], path: string, collect: Collector): void {
  const seen = new Set<string>();
  for (const item of items) {
    if (seen.has(item.block)) collect.errors.push(`${path} に同じブロックが複数あります: ${item.block}`);
    seen.add(item.block);
  }
}

function readItem(raw: unknown, path: string, collect: Collector): GuideItem | undefined {
  if (!isRecord(raw)) {
    collect.errors.push(`${path} はオブジェクトにしてください`);
    return undefined;
  }
  const block = collect.blockId(raw.block, `${path}.block`);
  const count = collect.count(raw.count, `${path}.count`);
  const item: GuideItem = { block: block ?? '', count: count ?? 1 };
  const label = collect.text(raw.label, `${path}.label`, MAX_TITLE);
  const note = collect.text(raw.note, `${path}.note`, MAX_TEXT);
  if (label !== undefined) item.label = label;
  if (note !== undefined) item.note = note;
  return block !== undefined && count !== undefined ? item : undefined;
}

function readCommand(raw: unknown, path: string, collect: Collector): GuideCommand | undefined {
  if (!isRecord(raw)) {
    collect.errors.push(`${path} はオブジェクトにしてください`);
    return undefined;
  }
  const input = collect.text(raw.input, `${path}.input`, MAX_COMMAND, true);
  if (input !== undefined && !input.startsWith('/')) {
    collect.errors.push(`${path}.input は/で始めてください`);
    return undefined;
  }
  if (input === undefined) return undefined;
  const command: GuideCommand = { input };
  const note = collect.text(raw.note, `${path}.note`, MAX_TEXT);
  if (note !== undefined) command.note = note;
  return command;
}

function readImage(raw: unknown, path: string, collect: Collector): GuideImage | undefined {
  if (!isRecord(raw)) {
    collect.errors.push(`${path} はオブジェクトにしてください`);
    return undefined;
  }
  const src = collect.text(raw.src, `${path}.src`, MAX_IMAGE_SRC, true);
  if (src === undefined) return undefined;
  if (!/^(https:\/\/|data:image\/)/.test(src)) {
    collect.errors.push(`${path}.src はhttps:// のURLか data:image/ のデータURLにしてください`);
    return undefined;
  }
  const image: GuideImage = { src };
  const alt = collect.text(raw.alt, `${path}.alt`, MAX_TEXT);
  if (alt !== undefined) image.alt = alt;
  return image;
}

function readStep(raw: unknown, path: string, collect: Collector): GuideStep | undefined {
  if (!isRecord(raw)) {
    collect.errors.push(`${path} はオブジェクトにしてください`);
    return undefined;
  }
  const title = collect.text(raw.title, `${path}.title`, MAX_TITLE, true);
  if (title === undefined) return undefined;
  const step: GuideStep = { title };

  const summary = collect.text(raw.summary, `${path}.summary`, MAX_TEXT);
  if (summary !== undefined) step.summary = summary;

  if (raw.ops !== undefined) {
    if (!Array.isArray(raw.ops) || raw.ops.length > MAX_OPS) {
      collect.errors.push(`${path}.ops は${MAX_OPS}件以内の配列にしてください`);
    } else {
      const ops = raw.ops
        .map((op, index) => collect.text(op, `${path}.ops[${index}]`, MAX_TEXT, true))
        .filter((op): op is string => op !== undefined);
      if (ops.length > 0) step.ops = ops;
    }
  }

  if (raw.highlight !== undefined) {
    if (!Array.isArray(raw.highlight) || raw.highlight.length > MAX_HIGHLIGHT) {
      collect.errors.push(`${path}.highlight は${MAX_HIGHLIGHT}件以内の配列にしてください`);
    } else {
      const highlight = raw.highlight
        .map((id, index) => collect.blockId(id, `${path}.highlight[${index}]`))
        .filter((id): id is string => id !== undefined);
      if (highlight.length > 0) step.highlight = highlight;
    }
  }

  if (raw.items !== undefined) {
    if (!Array.isArray(raw.items) || raw.items.length > MAX_ITEMS) {
      collect.errors.push(`${path}.items は${MAX_ITEMS}件以内の配列にしてください`);
    } else {
      const items = raw.items
        .map((item, index) => readItem(item, `${path}.items[${index}]`, collect))
        .filter((item): item is GuideItem => item !== undefined);
      checkDuplicateBlocks(items, `${path}.items`, collect);
      if (items.length > 0) step.items = items;
    }
  }

  if (raw.command !== undefined && raw.command !== null) {
    const command = readCommand(raw.command, `${path}.command`, collect);
    if (command !== undefined) step.command = command;
  }

  const check = collect.text(raw.check, `${path}.check`, MAX_TEXT);
  if (check !== undefined) step.check = check;
  const trouble = collect.text(raw.trouble, `${path}.trouble`, MAX_TEXT);
  if (trouble !== undefined) step.trouble = trouble;

  if (raw.image !== undefined && raw.image !== null) {
    const image = readImage(raw.image, `${path}.image`, collect);
    if (image !== undefined) step.image = image;
  }

  return step;
}

/**
 * 未知のキーは黙って捨て、返すオブジェクトには検証済みの値だけを持たせる。
 * 前方互換（古いビューアが新しいガイドを開く）のため、知らないキーはエラーにしない。
 */
export function validateGuide(raw: unknown): GuideValidation {
  if (!isRecord(raw)) return { errors: ['ガイドはJSONオブジェクトにしてください'] };
  if (raw.version !== 1) return { errors: ['version は 1 にしてください'] };

  const collect = new Collector();
  const title = collect.text(raw.title, 'title', MAX_TITLE, true);

  let course: LessonGuide['course'];
  if (raw.course !== undefined && raw.course !== null) {
    if (typeof raw.course === 'string' && (COURSES as readonly string[]).includes(raw.course)) {
      course = raw.course as LessonGuide['course'];
    } else {
      collect.errors.push(`course は ${COURSES.join(' / ')} のいずれかにしてください`);
    }
  }
  const lessonId = collect.text(raw.lessonId, 'lessonId', MAX_TITLE);

  let items: GuideItem[] = [];
  // アイテム集めの無い回（MakeCode中心の授業など）は空配列でよい。
  if (!Array.isArray(raw.items) || raw.items.length > MAX_ITEMS) {
    collect.errors.push(`items は${MAX_ITEMS}件以内の配列にしてください`);
  } else {
    items = raw.items
      .map((item, index) => readItem(item, `items[${index}]`, collect))
      .filter((item): item is GuideItem => item !== undefined);
    checkDuplicateBlocks(items, 'items', collect);
  }

  let steps: GuideStep[] = [];
  if (!Array.isArray(raw.steps) || raw.steps.length < 1 || raw.steps.length > MAX_STEPS) {
    collect.errors.push(`steps は1〜${MAX_STEPS}件の配列にしてください`);
  } else {
    steps = raw.steps
      .map((step, index) => readStep(step, `steps[${index}]`, collect))
      .filter((step): step is GuideStep => step !== undefined);
  }

  if (collect.errors.length > 0) return { errors: collect.errors };

  const guide: LessonGuide = { version: 1, title: title ?? '', items, steps };
  if (course !== undefined) guide.course = course;
  if (lessonId !== undefined) guide.lessonId = lessonId;
  return { guide };
}
